/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — "Save view…" / edit-view dialog (People Rebuild
   edit-session grant, 2026-08-17). Create a NEW saved view from the
   chart's current display config, or rename/re-scope/update-config an
   EXISTING one the caller already confirmed this user may edit
   (creator or admin — server enforces regardless, this just hides
   what would 403). Kit Dialog (Esc/focus-trap/overlay free), kit
   Button, SearchableSelect for the roles multi-pick (every
   pick-from-list is SearchableSelect — DESIGN.md law); multi-select is
   composed as "pick one to add" + removable chips, since the shared
   SearchableSelect primitive is single-value by design.

   Rename/visibility ONLY (Fix 4, Josh incident report 2026-08-17):
   Delete used to live in this dialog's footer, behind an unlabeled
   path (open the edit dialog, THEN find a delete button in the
   corner). It now lives directly in the views bar's "Manage" menu as
   its own kit ConfirmDialog — this dialog no longer offers delete at
   all, so there is exactly one discoverable route to each action.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Icon } from '../../../../components/ui/Icon';
import { rem } from '../../../../lib/rem';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { SearchableSelect } from '../../../../components/SearchableSelect';
import type { Role } from '../../../../../shared/types';
import type { OrgChartView, OrgChartViewVisibility } from '../../../../../shared/entities';

type VisibilityMode = OrgChartViewVisibility['mode'];

export interface SaveViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initialView?: OrgChartView;
  roles: Role[];
  busy?: boolean;
  onSubmit: (name: string, visibility: OrgChartViewVisibility) => void;
}

export function SaveViewDialog({
  open,
  onOpenChange,
  mode,
  initialView,
  roles,
  busy = false,
  onSubmit,
}: SaveViewDialogProps) {
  const [name, setName] = useState('');
  const [visMode, setVisMode] = useState<VisibilityMode>('everyone');
  const [roleIds, setRoleIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    setName(initialView?.name || '');
    const vis = initialView?.visibility;
    setVisMode(vis?.mode || 'everyone');
    setRoleIds(vis?.mode === 'roles' ? vis.roleIds || [] : []);
  }, [open, initialView]);

  const availableRoles = roles.filter((r) => !roleIds.includes(r.id));
  const selectedRoles = roleIds
    .map((id) => roles.find((r) => r.id === id))
    .filter((r): r is Role => Boolean(r));

  function handleSubmit() {
    if (!name.trim()) return;
    const visibility: OrgChartViewVisibility =
      visMode === 'roles'
        ? { mode: 'roles', roleIds }
        : visMode === 'private'
          ? { mode: 'private' }
          : { mode: 'everyone' };
    onSubmit(name.trim(), visibility);
  }

  const segBtn = (active: boolean): React.CSSProperties => ({
    flex: 1,
    minHeight: 'var(--space-6)',
    padding: rem(6) + ' ' + rem(10),
    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
    fontWeight: active ? 600 : 500,
    background: active ? 'var(--ac)' : 'var(--sf)',
    color: active ? 'var(--color-text-inverse)' : 'var(--tx)',
    border: '1px solid var(--bd)',
    cursor: 'pointer',
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New view' : 'Edit view'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? "Creates a new, blank board — its own people, lines, junctions, and notes, entirely separate from Main and every other board. It starts from the current chart's look (card style, team boxes, snap); reporting lines stay Main-only."
              : 'Views save how the chart LOOKS (card style, team boxes, snap) — never who reports to whom.'}
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: rem(14) }}>
          <div>
            <label
              htmlFor="orgchart-view-name"
              style={{
                display: 'block',
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                fontWeight: 600,
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Name
            </label>
            <input
              id="orgchart-view-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Print layout, Compact by department…"
              style={{
                width: '100%',
                padding: rem(7) + ' ' + rem(10),
                borderRadius: rem(5),
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: 'var(--tx)',
                fontSize: 'var(--font-size-base)',
              }}
            />
          </div>

          <div>
            <span
              style={{
                display: 'block',
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                fontWeight: 600,
                color: 'var(--tm)',
                marginBottom: 'var(--space-1)',
              }}
            >
              Who can see this view
            </span>
            <div
              role="radiogroup"
              aria-label="View visibility"
              style={{ display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}
            >
              <button
                type="button"
                role="radio"
                aria-checked={visMode === 'everyone'}
                style={segBtn(visMode === 'everyone')}
                onClick={() => setVisMode('everyone')}
              >
                Everyone
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={visMode === 'roles'}
                style={segBtn(visMode === 'roles')}
                onClick={() => setVisMode('roles')}
              >
                Specific roles
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={visMode === 'private'}
                style={segBtn(visMode === 'private')}
                onClick={() => setVisMode('private')}
              >
                Only me
              </button>
            </div>
          </div>

          {visMode === 'roles' && (
            <div>
              <SearchableSelect
                value=""
                onChange={(id) => {
                  if (id) setRoleIds((prev) => [...prev, id]);
                }}
                options={availableRoles.map((r) => ({ value: r.id, label: r.name }))}
                placeholder="Add a role…"
                searchPlaceholder="Search roles…"
                clearable={false}
                noResultsText="No more roles"
              />
              {selectedRoles.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: rem(6),
                    marginTop: 'var(--space-2)',
                  }}
                >
                  {selectedRoles.map((r) => (
                    <span
                      key={r.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        padding: rem(3) + ' var(--space-2)',
                        borderRadius: 'var(--radius-full)',
                        background: 'var(--sa)',
                        border: '1px solid var(--bd)',
                        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                        fontWeight: 500,
                      }}
                    >
                      {r.name}
                      <button
                        type="button"
                        aria-label={`Remove ${r.name}`}
                        title={`Remove ${r.name}`}
                        onClick={() => setRoleIds((prev) => prev.filter((id) => id !== r.id))}
                        style={{
                          display: 'inline-flex',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--tm)',
                          padding: 0,
                        }}
                      >
                        <Icon icon={X} em={0.9167} aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {selectedRoles.length === 0 && (
                <p
                  style={{
                    fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                    color: 'var(--tm)',
                    marginTop: rem(6),
                  }}
                >
                  No roles picked yet — nobody but you will see this view until you add one.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={busy || !name.trim()}>
            {busy
              ? mode === 'create'
                ? 'Creating…'
                : 'Saving…'
              : mode === 'create'
                ? 'Create view'
                : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
